/**
 * Main-thread client for the off-main-thread force layout worker.
 *
 * The worker computes positions; this client applies them to Cytoscape
 * inside `cy.batch()` so render + style passes are coalesced. The main
 * thread is free during physics iteration.
 */
import type { Core, EdgeSingular } from 'cytoscape';
import type { WorkerLayoutParams } from './forceLayout.worker';

export interface ForceLayoutController {
  stop: () => void;
  reheat: (alpha?: number) => void;
  isRunning: () => boolean;
}

interface TickMessage {
  type: 'tick';
  positions: { id: string; x: number; y: number }[];
}
interface EndMessage {
  type: 'end';
}
type WorkerOutgoing = TickMessage | EndMessage;

export interface ForceLayoutOptions {
  params: WorkerLayoutParams;
  /** Fires on first tick. */
  onStart?: () => void;
  /** Fires when the worker reports convergence or stop(). */
  onStop?: () => void;
  /**
   * If true, animate position changes via Cytoscape's animation system.
   * Keep false for large graphs (2k+ nodes) — per-frame raw set is faster.
   */
  animate?: boolean;
}

export function runForceLayoutInWorker(
  cy: Core,
  opts: ForceLayoutOptions,
): ForceLayoutController {
  // Snapshot nodes + links from the current Cytoscape state, preserving
  // existing positions so the worker has a warm start (avoids a full
  // re-randomize that would make the graph jump on every re-run).
  const nodes = cy.nodes().map((n) => ({
    id: n.id(),
    x: n.position('x'),
    y: n.position('y'),
  }));
  const visibleEdges = cy.edges().filter((e) => e.style('display') !== 'none');
  const links = visibleEdges.map((e) => {
    const edge = e as EdgeSingular;
    return {
      source: edge.source().id(),
      target: edge.target().id(),
    };
  });

  // Build the worker. Vite's `new URL(..., import.meta.url)` + `type: 'module'`
  // bundles the worker file as a separate chunk and emits a blob/asset URL.
  // Works in dev (ESM) and production (chunk asset). Fargate-served nginx
  // ships the chunk with the rest of the static assets — no special config.
  const worker = new Worker(
    new URL('./forceLayout.worker.ts', import.meta.url),
    { type: 'module' },
  );

  let running = true;
  let firstTick = true;

  worker.onmessage = (ev: MessageEvent<WorkerOutgoing>) => {
    const msg = ev.data;
    if (msg.type === 'tick') {
      if (firstTick) {
        firstTick = false;
        opts.onStart?.();
      }
      // Batch-apply positions inside cy.batch() to coalesce a single render.
      cy.batch(() => {
        for (const p of msg.positions) {
          const el = cy.getElementById(p.id);
          if (el.nonempty()) el.position({ x: p.x, y: p.y });
        }
      });
    } else if (msg.type === 'end') {
      running = false;
      opts.onStop?.();
      worker.terminate();
    }
  };

  worker.onerror = (ev) => {
    // Surface the failure so the UI stops spinning instead of hanging.
    // eslint-disable-next-line no-console
    console.error('[forceLayoutClient] worker error', ev);
    running = false;
    opts.onStop?.();
    try { worker.terminate(); } catch { /* ignore */ }
  };

  worker.postMessage({ type: 'start', nodes, links, params: opts.params });

  return {
    stop: () => {
      if (!running) return;
      running = false;
      worker.postMessage({ type: 'stop' });
      // Safety net in case the worker doesn't respond within a frame.
      setTimeout(() => {
        try { worker.terminate(); } catch { /* ignore */ }
      }, 100);
      opts.onStop?.();
    },
    reheat: (alpha = 1) => {
      if (!running) return;
      worker.postMessage({ type: 'reheat', alpha });
    },
    isRunning: () => running,
  };
}
